import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { IonIcon, IonSpinner } from '@ionic/angular';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  archiveOutline,
  checkmarkOutline,
  chatbubblesOutline,
  closeOutline,
  contractOutline,
  expandOutline,
  linkOutline,
  searchOutline,
  sparklesOutline
} from 'ionicons/icons';
import { Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import { AgentBotRow, AgentPurgedRange, AgentSessionEvent, ChatChainItem, ChatLegacyThreadRow, ChatThreadView } from '../../models/agents.models';
import { AGENTS_EVENTS_PER_READ, AgentsApiService } from '../../services/agents/agents-api.service';
import { AgentsFeedbackStore, FeedbackView } from '../../services/agents/agents-feedback.store';
import { AuthService } from '../../services/auth.service';
import { ChatLinkTarget, ChatOverlayMode, ChatService } from '../../services/chat.service';
import { DateFormatService } from '../../services/date-format.service';
import { LoggerService } from '../../services/logger.service';
import { TranslationParams, TranslationService } from '../../services/translation.service';
import { AgentAttachmentChipsComponent } from '../agents/agent-attachment-chips/agent-attachment-chips.component';
import { AgentMessageActionsComponent } from '../agents/agent-message-actions/agent-message-actions.component';
import { CopyOutcome } from '../agents/agents-clipboard';
import { TextRef, TranscriptEntry, buildTranscript, foldSteps } from '../agents/agents-chat/agents-chat.model';
import {
  AgentsChainLink,
  AgentsConversationComponent,
  AgentsRecordLink,
  AgentsSessionOpened,
  THREAD_START_KEY
} from '../agents/agents-conversation/agents-conversation.component';
import { AgentsMarkdownComponent, AgentsMarkdownLabels } from '../agents/agents-markdown/agents-markdown.component';
import { LegacySendRequest, LegacyThreadViewComponent } from './legacy-thread-view.component';

/** What the panel shows: a bot's one ongoing thread, or an old ChatKit chat (read only). */
export type SoloChatSelection = { kind: 'bot'; agentId: string | null } | { kind: 'legacy'; threadId: string };

/** An earlier session of the thread, shown read-only above the head (C0B-DESIGN 3.3). */
export interface EarlierSession {
  sessionId: string;
  /** The thin divider above it: when this session took over from the one before. */
  divider: string;
  entries: TranscriptEntry[];
  loading: boolean;
  failed: boolean;
  /** The session has more events than one read shows: only its newest are drawn, and it says so (AB#20941). */
  partial?: boolean;
}

/** The four starter prompts of an empty thread (the same four the ChatKit panel offered), by catalogue key. */
export const SOLO_STARTER_KEYS = ['week', 'reach', 'overdue', 'today'] as const;

const AGENTS_NAMESPACE = 'agents';
/** Earlier history is read until about this many messages are shown; older sessions load on scroll-up. */
const EARLIER_TARGET_ENTRIES = 50;
/** At most this many earlier sessions are read when the thread opens (each may be one short exchange). */
const EARLIER_INITIAL_SESSIONS = 6;
/** Pages of one session's events read at most (500 seqs a page). */
const EVENT_PAGES_PER_SESSION = 10;
/** Scrolled to within this many pixels of the top: the next older session loads. */
const LOAD_OLDER_THRESHOLD_PX = 80;
const LINK_COPIED_MS = 2000;
/** At or below this width every panel size is full screen (app.component.scss), so nothing behind it shows. */
const FULL_SCREEN_MAX_WIDTH = '(max-width: 980px)';
/** Ionic overlays (an alert, a popover, a select) handle their own Escape; the panel then leaves it to them. */
const OPEN_IONIC_OVERLAY = ['ion-alert', 'ion-popover', 'ion-action-sheet', 'ion-modal', 'ion-picker', 'ion-loading']
  .map(tag => `${tag}:not(.overlay-hidden)`).join(', ');

/**
 * The Solo chat panel (Channels C0b P5, C0B-DESIGN 3.1): the app shell hosts it in the same panel, expanded and full
 * screen geometry as the ChatKit panel it replaces where the chat surface is `agents`.
 *
 * - The header names the bot (Solo by default) and has the size buttons, Copy chat link and Close.
 * - The side column lists the person's bots, the Solo bot pinned first, and their earlier ChatKit chats (read only); one
 *   search box filters both. It is a drawer in the 400 px panel and on phones, a column when expanded or full screen.
 * - A bot is one ongoing thread (agents/chat/open). The head session is the router-free `app-agents-conversation`; the
 *   earlier sessions of the chain are stitched above it, oldest first, with a thin date divider where one session hands
 *   over to the next, and older ones load on scroll-up (agents/chat/chain_page).
 * - An empty thread shows the greeting and the four starter prompts, which send at once.
 * - Chat links (ChatService.openLink) resolve through agents/chat/resolve_link (C0B-DESIGN 3.5).
 *
 * As the conversation's host it: gives it the thread's start key ('thread:v1', so the first message, the files attached
 * before it and a second tab all meet the one root session agents/chat/open names), opens a result card's record in the
 * app (recordOpened: the panel stays open on a wide screen and closes where it covers the page), passes a continuation
 * or a new session straight back as the head (headChanged, sessionOpened), and hosts it in panel layout (its own scroll
 * box, pinned composer). One Talk at a time across the portal is the conversation's VoiceTalkLock.
 */
@Component({
  selector: 'app-solo-chat-overlay',
  standalone: true,
  imports: [IonIcon, IonSpinner, AgentsConversationComponent, AgentAttachmentChipsComponent, AgentsMarkdownComponent, LegacyThreadViewComponent,
    AgentMessageActionsComponent],
  templateUrl: './solo-chat-overlay.component.html',
  styleUrls: ['./solo-chat-overlay.component.scss']
})
export class SoloChatOverlayComponent implements OnInit, OnDestroy {
  @ViewChild(AgentsConversationComponent) conversation?: AgentsConversationComponent;
  @ViewChild(AgentsConversationComponent, { read: ElementRef }) conversationBox?: ElementRef<HTMLElement>;

  mode: ChatOverlayMode = 'panel';
  /** The conversation's start key: the bot's one ongoing thread (C0B-DESIGN 3.3 step 2). */
  readonly threadStartKey = THREAD_START_KEY;
  /** Whether the panel covers the whole screen at every size (phones and tablets); a seam for tests. */
  coversPage: () => boolean = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(FULL_SCREEN_MAX_WIDTH).matches;
  /** Whether an Ionic overlay (alert, popover, action sheet, modal, picker, loading) is open; a seam for tests. */
  ionicOverlayOpen: () => boolean = () => typeof document !== 'undefined' && !!document.querySelector(OPEN_IONIC_OVERLAY);
  /** The side column as a drawer (the 400 px panel and phones). */
  sideOpen = false;
  query = '';

  bots: AgentBotRow[] = [];
  shownBots: AgentBotRow[] = [];
  botsLoading = false;
  legacy: ChatLegacyThreadRow[] = [];
  legacyLoading = false;
  legacyRows: { threadId: string; title: string; when: string }[] = [];

  selection: SoloChatSelection | null = null;
  thread: ChatThreadView | null = null;
  threadLoading = false;
  threadError = '';

  /** The conversation's inputs: the bot, its name, the thread oldest first ending at the head, and the head. */
  agentId: string | null = null;
  agentName: string | null = null;
  chain: AgentsChainLink[] = [];
  headSessionId: string | null = null;
  headDivider = '';

  earlier: EarlierSession[] = [];
  loadingOlder = false;
  /** Older sessions of the chain not read yet, newest first (the next one to read is the first). */
  private unread: ChatChainItem[] = [];
  private olderCursor: string | null = null;

  /** "This chat is no longer available" (a link to a missing or someone else's chat), shown over Solo's thread. */
  notice = '';
  agentStateNotice = '';
  title = '';
  greeting = '';
  starters: { key: string; text: string }[] = [];
  linkCopied = false;

  /** AB#20941: the person's feedback on the earlier sessions' answers (`sessionId:seq`), rebuilt once per change. */
  feedbackMap: Record<string, FeedbackView | undefined> = {};
  /** Per earlier session: why rating is refused there (signed in as someone else), or null. */
  feedbackRefusals: Record<string, string | null | undefined> = {};
  /** The words app-agents-markdown adds for its code-block Copy and the over-cap notice. */
  markdownLabels: AgentsMarkdownLabels | null = null;
  /** The status region for the earlier sessions' actions ("Copied", "Thanks for your feedback"). */
  liveStatus = '';

  private readonly feedback = inject(AgentsFeedbackStore);
  private firstName = '';
  private openSeq = 0;
  private linkTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly api: AgentsApiService,
    private readonly chat: ChatService,
    private readonly auth: AuthService,
    private readonly translations: TranslationService,
    private readonly dates: DateFormatService,
    private readonly logger: LoggerService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {
    addIcons({
      alertCircleOutline, archiveOutline, checkmarkOutline, chatbubblesOutline, closeOutline, contractOutline, expandOutline,
      linkOutline, searchOutline, sparklesOutline
    });
  }

  ngOnInit(): void {
    this.translations.loadNamespace(AGENTS_NAMESPACE).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.markdownLabels = {
        copyCode: this.t('agents.chat.message.copy_code'),
        copyCodeLang: lang => this.t('agents.chat.message.copy_code_lang', { lang }),
        copied: this.t('agents.chat.message.copied'),
        tooLong: this.t('agents.chat.message.too_long'),
        showRest: this.t('agents.chat.message.show_rest')
      };
      this.rebuild();
    });
    this.feedback.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.rebuildFeedback();
      this.cdr.markForCheck();
    });
    this.chat.overlayMode.pipe(takeUntil(this.destroy$)).subscribe(mode => {
      this.mode = mode;
    });
    this.auth.authSession$.pipe(take(1), takeUntil(this.destroy$)).subscribe(session => {
      this.firstName = session?.user?.name?.trim().split(/\s+/)[0] ?? '';
      this.rebuild();
    });
    this.search$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => void this.loadLegacy(query));

    // A link asked for before the panel opened is applied first; else the panel reopens where the person was (Solo the
    // first time). Links asked for while it is open are applied as they come.
    const first = this.chat.consumePendingLink();
    void this.applyLink(first ?? this.chat.rememberedSelection ?? { kind: 'solo' });
    this.chat.pendingLink.pipe(filter((link): link is ChatLinkTarget => !!link), takeUntil(this.destroy$)).subscribe(() => {
      const link = this.chat.consumePendingLink();
      if (link) void this.applyLink(link);
    });

    void this.loadBots();
    void this.loadLegacy('');
  }

  ngOnDestroy(): void {
    this.openSeq++;
    if (this.linkTimer) clearTimeout(this.linkTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  t(key: string, params?: TranslationParams): string {
    return this.translations.getTranslation(key, params, { namespace: AGENTS_NAMESPACE });
  }

  text(ref: TextRef): string {
    return this.t(ref.key, ref.params);
  }

  /** Which folded step groups of earlier sessions the person opened (Lane D GUI). */
  stepsOpen: Record<string, boolean> = {};

  toggleSteps(key: string): void {
    this.stepsOpen = { ...this.stepsOpen, [key]: !this.stepsOpen[key] };
  }

  /** "12 steps", "1 step", or "12 steps, 2 did not work". */
  stepsLabel(entry: { items: readonly unknown[]; failed: number }): string {
    const count = entry.items.length;
    if (entry.failed) return count === 1 ? this.t('agents.chat.steps.count_one_failed') : this.t('agents.chat.steps.count_failed', { count, failed: entry.failed });
    return this.t(count === 1 ? 'agents.chat.steps.count_one' : 'agents.chat.steps.count', { count });
  }

  /** A folded step's words: its `what` is a domain token (orders, accounts) that the catalog turns into a plain word. */
  stepText(ref: TextRef): string {
    const what = ref.params?.['what'];
    if (typeof what !== 'string') return this.text(ref);
    const key = `agents.chat.steps.what.${what}`;
    const word = this.t(key);
    return this.t(ref.key, { ...ref.params, what: word === key ? what : word });
  }

  // ------------------------------------------------------------------------------------------------ chrome

  /**
   * Escape closes the innermost open thing first: an Ionic overlay handles its own, then the conversation's "more" menu
   * or Run again warning, then the chats drawer, and only then the panel.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.ionicOverlayOpen()) return;
    if (this.conversation?.dismissInnermost()) return;
    if (this.sideOpen) {
      this.sideOpen = false;
      return;
    }
    this.close();
  }

  close(): void {
    this.chat.closeChat();
  }

  expandMode(): void {
    this.chat.expandOverlay();
  }

  collapseMode(): void {
    this.chat.collapseOverlay();
  }

  toggleSide(): void {
    this.sideOpen = !this.sideOpen;
  }

  onSearch(value: string): void {
    this.query = value;
    this.rebuild();
    this.search$.next(value.trim());
  }

  /** The link that reopens what the panel shows: the head session, the old chat, or the bot's thread. */
  get chatLink(): string | null {
    const selection = this.selection;
    if (!selection) return null;
    if (selection.kind === 'legacy') return `/chat?threadId=${encodeURIComponent(selection.threadId)}`;
    if (this.headSessionId) return `/chat?sessionId=${encodeURIComponent(this.headSessionId)}`;
    if (this.thread && !this.thread.isSolo) return `/chat?bot=${encodeURIComponent(this.thread.agentId)}`;
    return this.thread ? '/chat' : null;
  }

  /**
   * Copy chat link: the absolute /chat?sessionId=<head> link. It opens the thread again for the person who copied it
   * (resolve_link follows only the caller's own chain); anyone else who opens it gets the "no longer available" notice.
   */
  async copyLink(): Promise<void> {
    const path = this.chatLink;
    if (!path) return;
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    this.linkCopied = true;
    if (this.linkTimer) clearTimeout(this.linkTimer);
    this.linkTimer = setTimeout(() => {
      this.linkCopied = false;
      this.linkTimer = null;
    }, LINK_COPIED_MS);
  }

  // ------------------------------------------------------------------------------------------------ the side column

  async loadBots(): Promise<void> {
    this.botsLoading = true;
    const result = await this.api.searchBots(undefined, 1, 50);
    this.botsLoading = false;
    if (!result.ok) {
      this.logger.warn('SoloChatOverlay', 'loadBots', 'Bots not read', { code: result.code });
      return;
    }
    // The Solo bot is pinned first by its flag (never its name, which the business may change); the rest keep the
    // API's order.
    const items = result.data.items ?? [];
    this.bots = [...items.filter(bot => bot.isSolo), ...items.filter(bot => !bot.isSolo)];
    this.rebuild();
  }

  async loadLegacy(search: string): Promise<void> {
    this.legacyLoading = true;
    const result = await this.api.chatLegacyList(search || null, 1, 25);
    if (search !== this.query.trim()) return;
    this.legacyLoading = false;
    if (!result.ok) {
      this.logger.warn('SoloChatOverlay', 'loadLegacy', 'Earlier chats not read', { code: result.code });
      this.legacy = [];
    } else {
      this.legacy = result.data.items ?? [];
    }
    this.rebuild();
  }

  selectBot(bot: AgentBotRow): void {
    this.sideOpen = false;
    this.notice = '';
    void this.openBot(bot.agentId);
  }

  selectLegacy(threadId: string): void {
    this.sideOpen = false;
    this.notice = '';
    this.openLegacy(threadId);
  }

  isSelectedBot(agentId: string): boolean {
    return this.selection?.kind === 'bot' && this.selection.agentId === agentId;
  }

  isSelectedLegacy(threadId: string): boolean {
    return this.selection?.kind === 'legacy' && this.selection.threadId === threadId;
  }

  // ------------------------------------------------------------------------------------------------ links

  /** Opens what a chat link names (C0B-DESIGN 3.5). */
  async applyLink(target: ChatLinkTarget): Promise<void> {
    this.notice = '';
    switch (target.kind) {
      case 'solo': await this.openBot(null); return;
      case 'bot': await this.openBot(target.agentId, null, true); return;
      case 'thread': await this.resolve({ threadId: target.threadId }); return;
      case 'session': await this.resolve({ sessionId: target.sessionId }); return;
    }
  }

  private async resolve(link: { threadId: string } | { sessionId: string }): Promise<void> {
    const seq = ++this.openSeq;
    this.threadLoading = true;
    const result = await this.api.chatResolveLink(link);
    if (seq !== this.openSeq) return;
    const resolved = result.ok ? result.data : null;
    if (resolved?.target === 'legacy' && resolved.threadId) {
      this.threadLoading = false;
      this.openLegacy(resolved.threadId);
      return;
    }
    if (resolved?.target === 'thread' && resolved.agentId) {
      await this.openBot(resolved.agentId, resolved.headSessionId);
      return;
    }
    // A missing chat and someone else's get the same answer: the notice, over Solo's thread.
    if (!result.ok) this.logger.warn('SoloChatOverlay', 'resolve', 'Chat link not resolved', { code: result.code });
    await this.openBot(null);
    this.notice = this.t('agents.solo_chat.unavailable');
  }

  // ------------------------------------------------------------------------------------------------ the thread

  /**
   * Opens a bot's one ongoing thread (agents/chat/open; no bot is Solo). Another bot's thread replaces the open one
   * only once it is read: until then the old conversation is gone (a spinner shows), so nothing typed in the meantime
   * can go to the previous bot. A resolved link's head that is not in the thread's newest page is opened as it is.
   * A /chat?bot= link to a bot that cannot be opened gets the notice over Solo's thread. True when the thread opened
   * and nothing newer was asked for meanwhile.
   */
  async openBot(agentId: string | null, resolvedHead: string | null = null, fallBackToSolo = false): Promise<boolean> {
    const seq = ++this.openSeq;
    const current = this.thread;
    const sameBot = !!current && (agentId === null ? current.isSolo : current.agentId === agentId);
    this.selection = { kind: 'bot', agentId };
    this.threadLoading = true;
    this.threadError = '';
    if (!sameBot) this.clearThread();
    this.rebuild();
    const result = await this.api.chatOpen(agentId);
    if (seq !== this.openSeq) return false;
    this.threadLoading = false;
    if (!result.ok) {
      if (fallBackToSolo && agentId) {
        const opened = await this.openBot(null);
        this.notice = this.t('agents.solo_chat.unavailable');
        return opened;
      }
      this.clearThread();
      this.threadError = result.code === 'impersonation_refused' || result.code === 'not_session_runas'
        ? this.t(`agents.chat.errors.${result.code}`)
        : this.t('agents.solo_chat.open_failed');
      this.logger.warn('SoloChatOverlay', 'openBot', 'Chat thread not opened', { code: result.code });
      this.rebuild();
      return false;
    }
    this.applyThread(result.data, resolvedHead);
    await this.fillEarlier(seq);
    return seq === this.openSeq;
  }

  /** No thread is shown (another bot's is being read, or it could not be opened): the conversation goes away. */
  private clearThread(): void {
    this.thread = null;
    this.headSessionId = null;
    this.chain = [];
    this.earlier = [];
    this.unread = [];
    this.olderCursor = null;
    this.headDivider = '';
  }

  /** Shows a thread agents/chat/open answered: the head is the conversation, the earlier sessions wait to be read. */
  private applyThread(thread: ChatThreadView, resolvedHead: string | null): void {
    this.threadLoading = false;
    this.threadError = '';
    this.thread = thread;
    this.selection = { kind: 'bot', agentId: thread.agentId };
    this.agentId = thread.agentId;
    this.agentName = thread.agentName;
    this.chat.rememberSelection(thread.isSolo ? { kind: 'solo' } : { kind: 'bot', agentId: thread.agentId });
    this.earlier = [];
    const newestFirst = thread.chain ?? [];
    if (resolvedHead && !newestFirst.some(link => link.sessionId === resolvedHead)) {
      this.headSessionId = resolvedHead;
      this.chain = [{ sessionId: resolvedHead }];
      this.unread = [];
      this.olderCursor = null;
      this.headDivider = '';
    } else {
      this.headSessionId = thread.headSessionId;
      this.chain = [...newestFirst].reverse().map(link => ({ sessionId: link.sessionId, state: link.state as AgentsChainLink['state'],
        createdOn: link.createdOn, endedOn: link.endedOn }));
      this.unread = newestFirst.slice(1);
      this.olderCursor = thread.olderCursor;
      this.headDivider = newestFirst.length > 1 && newestFirst[0].createdOn ? this.divider(newestFirst[0].createdOn) : '';
    }
    this.rebuild();
  }

  private openLegacy(threadId: string): void {
    this.openSeq++;
    this.selection = { kind: 'legacy', threadId };
    this.threadLoading = false;
    this.threadError = '';
    this.chat.rememberSelection({ kind: 'thread', threadId });
    this.rebuild();
  }

  /** Retry after the thread could not be opened. */
  retry(): void {
    const selection = this.selection;
    void this.openBot(selection?.kind === 'bot' ? selection.agentId : null);
  }

  /**
   * A message typed under an old chat goes to Solo's thread (Solo does not see the old chat). The old chat stays on
   * screen, with the words still typed, until Solo's thread is read; the words are sent only to Solo, never to a bot
   * the person picked meanwhile, and the old chat's box is cleared only once they were sent (request.resolve(true)).
   * If they could not be sent after Solo's thread opened, they wait in its composer.
   */
  async sendToSolo(request: LegacySendRequest): Promise<void> {
    request.resolve(await this.sendFromLegacy(request.text));
  }

  private async sendFromLegacy(text: string): Promise<boolean> {
    if (!(this.selection?.kind === 'bot' && this.thread?.isSolo && !this.threadLoading)) {
      const seq = ++this.openSeq;
      const result = await this.api.chatOpen(null);
      if (seq !== this.openSeq) return false;
      if (!result.ok || !result.data.isSolo) {
        this.notice = this.t('agents.solo_chat.open_failed');
        this.logger.warn('SoloChatOverlay', 'sendFromLegacy', 'Solo thread not opened', { code: result.ok ? 'not_solo' : result.code });
        this.rebuild();
        return false;
      }
      this.selection = { kind: 'bot', agentId: result.data.agentId };
      this.applyThread(result.data, null);
      void this.fillEarlier(seq);
    }
    return this.sendThroughConversation(text, this.thread!.agentId);
  }

  async sendStarter(key: string): Promise<void> {
    if (this.agentId) await this.sendThroughConversation(this.t(`agents.solo_chat.starter.${key}`), this.agentId);
  }

  /** Sends through the conversation, only when it is the given bot's (false otherwise, or when the send failed). */
  private async sendThroughConversation(text: string, agentId: string): Promise<boolean> {
    // The conversation is created (or given its new inputs) by this change detection, and opens its head at once.
    this.cdr.detectChanges();
    const conversation = this.conversation;
    if (!conversation || conversation.agentId !== agentId || this.thread?.agentId !== agentId) return false;
    return conversation.sendText(text);
  }

  // ------------------------------------------------------------------------------------------------ host duties

  /**
   * A message went to the continuation of the ended head (decision 14), or voice moved on: the new session is the head
   * at once, and the session it continued joins the earlier history above it.
   */
  onHeadChanged(sessionId: string): void {
    this.followHead(sessionId);
  }

  /**
   * The conversation started the thread's first session (its first message with the thread's key). It is passed
   * straight back as the head. That session is the thread's root (agents/chat/open's rootSessionId); when it is not
   * (the start found the thread already begun in another tab and the words went to its head), the thread is read
   * again so the panel shows the whole chain. Run again is not offered in the panel (it would open a new chain).
   */
  onSessionOpened(opened: AgentsSessionOpened): void {
    this.followHead(opened.sessionId);
    const thread = this.thread;
    if (thread && opened.sessionId !== thread.rootSessionId) {
      this.logger.info('SoloChatOverlay', 'onSessionOpened', 'Opened session is not the thread root; reading the thread again', { via: opened.via });
      void this.openBot(thread.isSolo ? null : thread.agentId);
    }
  }

  /** A result card's record: opened in the app with the router (no page load: the draft, Talk and stream live on). */
  onRecordOpened(link: AgentsRecordLink): void {
    void this.router.navigate(link.path, { queryParams: link.queryParams });
    // On a phone or tablet every panel size covers the page, so the panel closes to show the record (the chat is kept
    // on the server and reopens where it was). On a wide screen it stays open; expanded or full screen would hide the
    // record, so it steps down to the side panel.
    if (this.coversPage()) this.chat.closeChat();
    else if (this.mode !== 'panel') this.chat.setOverlayMode('panel');
  }

  private followHead(sessionId: string): void {
    const previous = this.headSessionId;
    if (previous === sessionId) return;
    this.headSessionId = sessionId;
    if (!this.chain.some(link => link.sessionId === sessionId)) this.chain = [...this.chain, { sessionId }];
    if (this.thread?.isSolo) this.chat.rememberSelection({ kind: 'solo' });
    else if (this.agentId) this.chat.rememberSelection({ kind: 'bot', agentId: this.agentId });
    if (previous) {
      const handedOver = new Date().toISOString();
      const session: EarlierSession = { sessionId: previous, divider: this.earlier.length ? this.headDivider : '', entries: [], loading: true, failed: false };
      this.earlier = [...this.earlier, session];
      this.headDivider = this.divider(handedOver);
      void this.readInto(session).then(() => this.feedback.load([session.sessionId]));
    }
    this.rebuild();
  }

  // ------------------------------------------------------------------------------------------------ earlier history

  /** Reads earlier sessions until about {@link EARLIER_TARGET_ENTRIES} messages are shown, or none are left. */
  private async fillEarlier(seq: number): Promise<void> {
    let sessions = 0;
    const read: string[] = [];
    while (seq === this.openSeq && sessions < EARLIER_INITIAL_SESSIONS && this.hasOlder
        && this.earlier.reduce((sum, session) => sum + session.entries.length, 0) < EARLIER_TARGET_ENTRIES) {
      const loaded = await this.loadOlder(seq, false);
      if (!loaded) break;
      read.push(this.earlier[0].sessionId);
      sessions++;
    }
    // AB#20941: the person's feedback for the sessions just read, in one batch (at most 25 per call).
    if (read.length && seq === this.openSeq) void this.feedback.load(read);
  }

  get hasOlder(): boolean {
    return this.unread.length > 0 || !!this.olderCursor;
  }

  /** Scrolled near the top of the conversation: the next older session loads, keeping the reader where they were. */
  onConversationScroll(): void {
    const box = this.conversationBox?.nativeElement;
    if (!box || box.scrollTop > LOAD_OLDER_THRESHOLD_PX || this.loadingOlder || !this.hasOlder) return;
    void this.loadOlderKeepingPlace();
  }

  /** "Show earlier messages" (the same as scrolling up, for a keyboard or a screen reader). */
  async loadOlderKeepingPlace(): Promise<void> {
    const box = this.conversationBox?.nativeElement;
    const before = box ? { height: box.scrollHeight, top: box.scrollTop } : null;
    const loaded = await this.loadOlder(this.openSeq);
    if (!loaded || !box || !before) return;
    this.cdr.detectChanges();
    box.scrollTop = before.top + (box.scrollHeight - before.height);
  }

  /** Reads the next older session (asking chain_page for more when the listed ones are all read) and puts it first. */
  private async loadOlder(seq: number, loadFeedback = true): Promise<boolean> {
    if (this.loadingOlder || !this.agentId) return false;
    this.loadingOlder = true;
    try {
      if (!this.unread.length && this.olderCursor) {
        const page = await this.api.chatChainPage(this.agentId, this.olderCursor);
        if (seq !== this.openSeq) return false;
        if (!page.ok) {
          this.logger.warn('SoloChatOverlay', 'loadOlder', 'Older sessions not read', { code: page.code });
          this.olderCursor = null;
          return false;
        }
        this.unread = [...page.data.chain];
        this.olderCursor = page.data.olderCursor;
        // Keep when each session started: the divider above the session that took over from it says so.
        this.chain = [...[...page.data.chain].reverse().map(link => ({ sessionId: link.sessionId,
          state: link.state as AgentsChainLink['state'], createdOn: link.createdOn, endedOn: link.endedOn })), ...this.chain];
      }
      const next = this.unread.shift();
      if (!next) return false;
      const handedOverTo = this.earlier[0];
      const session: EarlierSession = { sessionId: next.sessionId, divider: '', entries: [], loading: true, failed: false };
      // The session that came after this one now starts with a divider saying when it took over.
      if (handedOverTo && !handedOverTo.divider) handedOverTo.divider = this.divider(this.startedOn(handedOverTo.sessionId));
      this.earlier = [session, ...this.earlier];
      this.rebuild();
      await this.readInto(session);
      if (loadFeedback && seq === this.openSeq) void this.feedback.load([session.sessionId]);
      return seq === this.openSeq;
    } finally {
      this.loadingOlder = false;
    }
  }

  /** When a session of the chain started, as far as the chain answers said. */
  private startedOn(sessionId: string): string {
    return this.chain.find(link => link.sessionId === sessionId)?.createdOn
      ?? this.thread?.chain.find(link => link.sessionId === sessionId)?.createdOn ?? '';
  }

  /** Reads one session's committed events (paged) and builds its read-only transcript. */
  private async readInto(session: EarlierSession): Promise<void> {
    let events: AgentSessionEvent[] = [];
    let purged: AgentPurgedRange[] = [];
    let fromSeq = 1;
    let failed = false;
    let partial = false;
    for (let page = 0; page < EVENT_PAGES_PER_SESSION; page++) {
      const slice = await this.api.events(session.sessionId, fromSeq);
      if (!slice.ok) {
        failed = true;
        break;
      }
      // AB#20941 (MESSAGE-ACTIONS-DESIGN 4.3): a session longer than one read shows its NEWEST events, next to the
      // session that continued it, and says that older messages are not shown (never a silent cut).
      const limit = EVENT_PAGES_PER_SESSION * AGENTS_EVENTS_PER_READ;
      if (page === 0 && fromSeq === 1 && slice.data.lastSeq > limit) {
        partial = true;
        fromSeq = slice.data.lastSeq - limit + 1;
        page = -1;
        events = [];
        purged = [];
        continue;
      }
      events.push(...(slice.data.events ?? []));
      purged.push(...(slice.data.purged ?? []));
      if (slice.data.toSeq >= slice.data.lastSeq || slice.data.toSeq < fromSeq) break;
      fromSeq = slice.data.toSeq + 1;
    }
    // Earlier sessions are read only: their words, files and steps (cards and controls belong to the head).
    session.entries = foldSteps(buildTranscript(events, [], [], null, purged)
      .filter(entry => entry.kind === 'user' || entry.kind === 'agent' || entry.kind === 'activity'));
    session.loading = false;
    session.failed = failed;
    session.partial = partial;
    this.earlier = [...this.earlier];
    this.rebuild();
  }

  /** The feedback map and refusals the template reads, from the store (the same objects while nothing changed). */
  private rebuildFeedback(): void {
    const ids = this.earlier.map(session => session.sessionId);
    this.feedbackMap = this.feedback.snapshot(ids);
    const refusals: Record<string, string | null> = {};
    for (const id of ids) refusals[id] = this.feedback.refusal(id);
    this.feedbackRefusals = refusals;
  }

  /** Words for the status region; the same words twice are still announced. */
  say(words: string): void {
    this.liveStatus = words === this.liveStatus ? `${words}\u00a0` : words;
  }

  /** A code block's Copy in an earlier answer. */
  onCodeCopied(outcome: CopyOutcome): void {
    this.say(outcome === 'copied' ? this.t('agents.chat.message.copied') : this.t('agents.chat.message.copy_failed'));
  }

  private divider(at: string): string {
    return at ? this.t('agents.solo_chat.divider', { when: this.dates.formatDateTime(at) }) : '';
  }

  // ------------------------------------------------------------------------------------------------ view model

  get showStart(): boolean {
    return this.selection?.kind === 'bot' && !!this.thread && !this.threadLoading && !this.headSessionId && this.earlier.length === 0;
  }

  /** Recomputes everything the template reads. Called once per change, never from the template. */
  private rebuild(): void {
    const query = this.query.trim().toLowerCase();
    this.shownBots = query ? this.bots.filter(bot => bot.name.toLowerCase().includes(query)) : [...this.bots];
    this.legacyRows = this.legacy.map(row => ({
      threadId: row.threadId,
      title: row.title?.trim() || this.t('agents.solo_chat.legacy.untitled'),
      when: this.dates.formatDateOnly(row.modifiedOn ?? row.createdOn)
    }));
    const selected = this.selection;
    this.title = selected?.kind === 'legacy'
      ? this.t('agents.solo_chat.earlier_chats')
      : this.agentName && this.thread ? this.agentName : this.t('agents.solo_chat.title');
    this.agentStateNotice = this.thread && this.thread.agentState !== 'active' && selected?.kind === 'bot'
      ? this.t('agents.solo_chat.agent_unavailable', { name: this.thread.agentName })
      : '';
    this.greeting = this.firstName
      ? this.t('agents.solo_chat.greeting_named', { name: this.firstName })
      : this.t('agents.solo_chat.greeting');
    this.starters = SOLO_STARTER_KEYS.map(key => ({ key, text: this.t(`agents.solo_chat.starter.${key}`) }));
    this.rebuildFeedback();
  }
}
